import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from '@/components';
import '@/assets/styles/components/meet-provider-test-modal.styles.scss';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useFeatureFlag } from 'configcat-react';
import { If } from 'vite-plugin-react-control-statements';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { useMeeting } from '@/context/meeting-context';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import useDeviceInfo from '@/hooks/use-device-info.tsx';
import { isEmptyObject } from '@/utility';
import { useSizes } from "@/utility/responsive.ts";
import LocalStorageManager from '@/managers/local-storage-manager.tsx';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';
import { TimeSpan } from '@/utility/time-span.ts';

type IMeetProviderTestModal = object;

const PROVIDER_COUNT = 3;

const MeetProviderTestModal: React.FC<IMeetProviderTestModal> = () => {
	const { controlPermission } = useDevicePermissions();
	const navigate = useNavigate();

	const { showPastMeetingModal, studentConnectedModal, hasActiveMeeting, isConnectToastVisible } = useMeeting();

	const { ProviderFeedbackControllerApi } = useApi();

	const { deviceRequest } = useDeviceInfo();
    const { isMobile } = useSizes();

	const { value: meetingProviderTestConfig } = useFeatureFlag(CONFIGCAT_KEYS.MEETING_PROVIDER_TEST_CONFIG, '{}');

	const { data: interactionFeedback, isLoading: interactionFeedbackIsLoading } = useQueryProvider(
		[QUERY_KEYS.findProviderFeedbacks, deviceRequest?.deviceType],
		() => ProviderFeedbackControllerApi.findProviderFeedbacks(isMobile ? 'MOBILE': 'WEB'),
		{
			select: ({ data }) => data?.data
		}
	);

	const [isOpenModal, setIsOpenModal] = useState(false);

	const navigateToConnectionTest = () => {
		controlPermission(() => navigate(NavigationPath.meetProviderTest));
	};

	const meetingProviderTestConfigData = useMemo(() => {
		if (meetingProviderTestConfig) {
			return JSON.parse(meetingProviderTestConfig);
		}
	}, [meetingProviderTestConfig]);

	const isSnoozed = (): boolean => {
		const snoozedAt = LocalStorageManager.getLocalStorage<number>(LocalStorageKeys.MEET_PROVIDER_TEST_SNOOZED_AT);
		if (!snoozedAt) return false;
		return Date.now() - snoozedAt < TimeSpan.hours.one * 24;
	};

	const handleLater = () => {
		LocalStorageManager.setLocalStorage<number>(LocalStorageKeys.MEET_PROVIDER_TEST_SNOOZED_AT, Date.now());
		setIsOpenModal(false);
	};

	useEffect(() => {
		if (showPastMeetingModal || studentConnectedModal || hasActiveMeeting || isConnectToastVisible || interactionFeedbackIsLoading || isEmptyObject(meetingProviderTestConfigData)) {
			return;
		}

		if (meetingProviderTestConfigData?.showTestModal === false || interactionFeedback?.length === PROVIDER_COUNT || interactionFeedback?.some(item => item?.rating >= 4) || isSnoozed()) {
			setIsOpenModal(false);
		} else {
			setIsOpenModal(true);
		}
	}, [meetingProviderTestConfigData, showPastMeetingModal, studentConnectedModal, interactionFeedback, interactionFeedbackIsLoading, hasActiveMeeting, isConnectToastVisible]);

	return (
		<Modal
			isOpen={isOpenModal}
			onClose={() => setIsOpenModal(false)}
			closeOnEsc={false}
			closeOnClickOutside={false}
			size="md"
			variant="centered"
			className="meet-provider-test"
			bodyClassName="meet-provider-test-body">
			<div className="title">Bağlantı Testi Gerekli!</div>
			<div className="description">Görüşmelere sorunsuz katılabilmeniz için kamera ve mikrofon testi yapmanız gerekiyor. Lütfen testi başlatın.</div>
			<div className="devices">
				<div className="device">
					<i className="device-icon icons-microphone" />
					<span className="device-name">Mikrofon</span>
				</div>
				<div className="device">
					<i className="device-icon icons-videocamera-record" />
					<span className="device-name">Kamera</span>
				</div>
			</div>
			<div className="warning">
				<i className="icons-info-circle" />
				<span className="text">Bu test cihaz bazlıdır. Farklı bir cihazdan giriş yaptığınızda yeniden yapmanız istenir.</span>
			</div>
			<div className="actions">
				<If condition={meetingProviderTestConfigData?.showLaterButton}>
					<Button
						size="medium"
						variant="secondary"
						onClick={handleLater}>
						Daha Sonra Yap
					</Button>
				</If>
				<Button
					size="medium"
					variant="success"
					onClick={() => navigateToConnectionTest()}>
					Testi Başlat
				</Button>
			</div>
		</Modal>
	);
};

export default MeetProviderTestModal;
